import { useSessionStorageState } from '../lib';
import { environment } from '../../../environments/environment';
import { keycloak } from '@coverright/shared/keycloak';
import { useState } from 'react';

export type SavedSettings = {
  tableSettings?: {
    [key: string]: {
      viewName: string,
      views: any[] // StoredTableSettings
    }
  },
  menuCollapsed?: boolean,
}

export function useSavedSettings(): [SavedSettings, (value: Partial<SavedSettings>) => void, boolean] {
  const [settings, setSettings] = useSessionStorageState<SavedSettings>(
    'settings',
    {}
  )
  const [loading, setLoading] = useState(false);

  const saveSettings = async (value: Partial<SavedSettings>) => {
    setLoading(true);
    try {
      const updated = recursiveUpdate(settings, value, {
        addNewProperties: true
      });
      setSettings(updated);
      await fetch(
        `${environment.userFlowRestUrl}/saveAdminSettings`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${keycloak.token}`
          },
          body: JSON.stringify(updated)
        }
      )
    } finally {
      setLoading(false);
    }
  }

  return [settings, saveSettings, loading]
}


type JsonValue = string | number | boolean | null | JsonObject | JsonArray;
interface JsonObject { [key: string]: JsonValue }
type JsonArray = Array<JsonValue>

/**
 * Recursively updates a target JSON object with values from a source object
 * @param target The object to be updated
 * @param source The object containing updates
 * @param options Configuration options
 * @returns The updated target object
 */
function recursiveUpdate(
  target: JsonObject,
  source: JsonObject,
  options: {
    /** Whether to add new properties that exist in source but not in target (default: false) */
    addNewProperties?: boolean;
    /** Whether to delete properties that exist in target but not in source (default: false) */
    deleteMissingProperties?: boolean;
    /** Whether to merge arrays (default: false) */
    mergeArrays?: boolean;
  } = {}
): JsonObject {
  const {
    addNewProperties = false,
    deleteMissingProperties = false,
    mergeArrays = false,
  } = options;

  // Handle null/undefined source
  if (source == null) {
    return target;
  }

  // Handle arrays
  if (mergeArrays && Array.isArray(target) && Array.isArray(source)) {
    return [...target, ...source] as unknown as JsonObject;
  }

  // Create a new object to avoid mutating the original target
  const result: JsonObject = { ...target };

  // Process all keys in source
  for (const key in source) {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      const sourceValue = source[key];
      const targetValue = target[key];

      if (typeof sourceValue === 'object' && sourceValue !== null && !Array.isArray(sourceValue)) {
        // Nested object - recurse
        if (typeof targetValue === 'object' && targetValue !== null && !Array.isArray(targetValue)) {
          result[key] = recursiveUpdate(
            targetValue as JsonObject,
            sourceValue as JsonObject,
            options
          );
        } else if (addNewProperties) {
          result[key] = { ...(sourceValue as JsonObject) };
        }
      } else if (Array.isArray(sourceValue) && mergeArrays && Array.isArray(targetValue)) {
        // Arrays - merge if option enabled
        result[key] = [...(targetValue as JsonArray), ...(sourceValue as JsonArray)];
      } else {
        // Primitive value or array (without merging) - overwrite
        result[key] = sourceValue;
      }
    }
  }

  // Handle properties that exist in target but not in source
  if (deleteMissingProperties) {
    for (const key in result) {
      if (Object.prototype.hasOwnProperty.call(result, key) && !(key in source)) {
        delete result[key];
      }
    }
  }

  return result;
}
